/**
 * Books
 *
 * Component to handle the carousel, grid, and modal renderings.
 * Data is either automatic directly from the API or manually entered from the API via the editor.
 */

/**
 * React dependencies
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * DWT dependencies
 */
import { ModalContext } from '../Modal/ModalContext';
import { BooksContext } from './BooksContext';

/**
 * DWT hooks
 */

import useFetchPRHData from '../../hooks/useFetchPRHData';
import useModal from '../../hooks/useModal';

/**
 * DWT components
 */

import Carousel from '../Carousel';
import FilterContainer from '../Filter/FilterContainer';
import Grid from '../Grid';
import LoadMoreButton from '../LoadMoreButton';
import Modal from '../Modal/Modal';
import NoResults from '../NoResults';

const Books = () => {
  const c = useContext( BooksContext );
  const [ context, setContext ] = useState( c );

  const carousel = context.module === 'carousel';
  const grid = context.module === 'grid';

  const showMetadata = context.showMetadata === 'true';
  const modalRef = useRef( null );

  const { makeRequest, setStart, recordCount, loading, error, data } =
        useFetchPRHData( context, '/wp-json/dwt/v2/list-title' );

  const memoizedData = useMemo( () => data, [ data ] );

  const {
    modalClass,
    modalData,
    modalType,
    onClickOpenModal,
    onClickCloseModal
  } = useModal( memoizedData, modalRef, context );

  useEffect( () => {
    const targetNode = document.querySelector(
      '[ data-feature-by="books" ]'
    );
    const config = { attributes: true, childList: false, subtree: false };

    const callback = ( mutationList ) => {
      for ( const mutation of mutationList ) {
        if ( mutation.type === 'attributes' ) {
          setContext({ ...mutation.target.dataset });
        }
      }
    };

    const observer = new MutationObserver( callback );
    observer.observe( targetNode, config );
  }, [] );

  const handleErrorMsg = () =>
    context.listType === 'full-list'
      ? 'Sorry, we are unable to find what you\'re looking for. Try adjusting the filters.'
      : 'Sorry something happened. Please refresh the page.';

  return (
    <>
      <ModalContext.Provider value={{ onClickOpenModal }}>
        {context.listType === 'full-list' && (
          <FilterContainer
            filterType="api"
            context={context}
            recordCount={recordCount}
            setStart={setStart}
            error={error}
          />
        )}

        <div className="list-wrapper">
          {error && <NoResults handleErrorMsg={handleErrorMsg()} />}

          {carousel && (
            <Carousel
              context={context}
              data={memoizedData}
              showMetadata={showMetadata}
              error={error}
              loading={loading}
            />
          )}

          {grid && (
            <Grid
              context={context}
              data={memoizedData}
              showMetadata={showMetadata}
              error={error}
              loading={loading}
            />
          )}

          {!error &&
            context.listType === 'full-list' &&
            memoizedData?.length < recordCount && (
            <LoadMoreButton
              makeRequest={makeRequest}
              loading={loading}
            />
          )}

          <Modal
            context={context}
            modalData={modalData}
            modalClass={modalClass}
            modalType={modalType}
            onClose={onClickCloseModal}
            ref={modalRef}
          />
        </div>
      </ModalContext.Provider>
    </>
  );
};

const renderBooks = () => {
  const els = document.querySelectorAll( '[data-feature-by="books"]' );
  if ( !els ) {
    return;
  }

  for ( const el of els ) {
    const root = createRoot( el );
    root.render(
      <BooksContext.Provider value={{ ...el.dataset }}>
        <Books />
      </BooksContext.Provider>
    );
  }
};

export default renderBooks;
